import React from 'react';
import { motion } from 'framer-motion';

/**
 * Skeleton Component
 * 
 * A flexible skeleton loading component that creates smooth
 * animated placeholders for content. Designed for senior-friendly UX.
 */

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'wave' | 'none';
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  width,
  height,
  animation = 'pulse',
}) => {
  const baseClasses = 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200';
  
  const variantClasses = {
    text: 'h-4 rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-none',
    rounded: 'rounded-xl',
  };

  const animationClasses = {
    pulse: 'animate-pulse',
    wave: 'animate-shimmer',
    none: '',
  };

  const style: React.CSSProperties = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
  };

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${animationClasses[animation]} ${className}`}
      style={style}
    />
  );
};

/**
 * Page Skeleton Loader
 * 
 * A general-purpose page loading skeleton that shows
 * a friendly loading state while content loads.
 */
export const PageSkeleton: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
  <div className="flex flex-col items-center justify-center min-h-[300px] p-8">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="text-center"
    >
      <div className="relative mb-6">
        <div className="w-16 h-16 border-4 border-primary/20 rounded-full" />
        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-primary rounded-full animate-spin" />
      </div>
      <p className="text-lg text-text-secondary">{message}</p>
    </motion.div>
  </div>
);

/**
 * Card Skeleton
 * 
 * A skeleton for card-style components like reminders, games, etc.
 */
export const CardSkeleton: React.FC<{ count?: number }> = ({ count = 1 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.1 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-start gap-4">
          <Skeleton variant="circular" width={48} height={48} />
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="80%" height={16} />
            <Skeleton variant="text" width="40%" height={14} />
          </div>
        </div>
      </motion.div>
    ))}
  </div>
);

/**
 * Button Grid Skeleton
 * 
 * A skeleton for button grids like the home page quick actions.
 */
export const ButtonGridSkeleton: React.FC<{ columns?: number; count?: number }> = ({ 
  columns = 2, 
  count = 6 
}) => (
  <div className={`grid grid-cols-${columns} gap-4`}>
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: i * 0.05 }}
        className="bg-white rounded-2xl p-6 flex flex-col items-center gap-3"
      >
        <Skeleton variant="circular" width={56} height={56} />
        <Skeleton variant="text" width="70%" height={18} />
        <Skeleton variant="text" width="50%" height={14} />
      </motion.div>
    ))}
  </div>
);

/**
 * Chat Skeleton
 * 
 * A skeleton for chat message loading states.
 */
export const ChatSkeleton: React.FC = () => (
  <div className="space-y-4 p-4">
    {/* Received message skeleton */}
    <div className="flex gap-3">
      <Skeleton variant="circular" width={40} height={40} />
      <div className="space-y-2 max-w-[70%]">
        <Skeleton variant="rounded" width={200} height={40} />
        <Skeleton variant="text" width={80} height={12} />
      </div>
    </div>
    
    {/* Sent message skeleton */}
    <div className="flex gap-3 justify-end">
      <div className="space-y-2 max-w-[70%]">
        <Skeleton variant="rounded" width={180} height={36} className="ml-auto" />
        <Skeleton variant="text" width={60} height={12} className="ml-auto" />
      </div>
    </div>
    
    {/* Another received message */}
    <div className="flex gap-3">
      <Skeleton variant="circular" width={40} height={40} />
      <div className="space-y-2 max-w-[70%]">
        <Skeleton variant="rounded" width={240} height={60} />
      </div>
    </div>
  </div>
);

/**
 * Reminder Skeleton
 * 
 * A skeleton specifically for reminder list items.
 */
export const ReminderSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: i * 0.1 }}
        className="bg-white rounded-2xl p-4 flex items-center gap-4 shadow-sm"
      >
        <Skeleton variant="rounded" width={60} height={60} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="50%" height={18} />
          <div className="flex items-center gap-2">
            <Skeleton variant="rounded" width={24} height={24} />
            <Skeleton variant="text" width={80} height={16} />
          </div>
        </div>
        <Skeleton variant="circular" width={44} height={44} />
      </motion.div>
    ))}
  </div>
);

/**
 * Game Card Skeleton
 * 
 * A skeleton for game cards in the games section.
 */
export const GameCardSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-2 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: i * 0.08 }}
        className="bg-white rounded-2xl p-4 flex flex-col items-center gap-3"
      >
        <Skeleton variant="rounded" width="100%" height={100} />
        <Skeleton variant="text" width="60%" height={18} />
        <Skeleton variant="text" width="80%" height={14} />
        <Skeleton variant="rounded" width="100%" height={40} />
      </motion.div>
    ))}
  </div>
);

/**
 * List Skeleton
 * 
 * A generic list skeleton for various list views.
 */
export const ListSkeleton: React.FC<{ count?: number; showAvatar?: boolean }> = ({ 
  count = 5, 
  showAvatar = true 
}) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: i * 0.05 }}
        className="flex items-center gap-3 p-3 bg-white rounded-xl"
      >
        {showAvatar && <Skeleton variant="circular" width={44} height={44} />}
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="70%" height={16} />
          <Skeleton variant="text" width="40%" height={12} />
        </div>
        <Skeleton variant="text" width={24} height={24} />
      </motion.div>
    ))}
  </div>
);

/**
 * Stats Skeleton
 * 
 * A skeleton for dashboard-style statistics cards.
 */
export const StatsSkeleton: React.FC<{ count?: number }> = ({ count = 4 }) => (
  <div className="grid grid-cols-2 gap-4">
    {Array.from({ length: count }).map((_, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: i * 0.1 }}
        className="bg-white rounded-2xl p-4 text-center"
      >
        <Skeleton variant="circular" width={48} height={48} className="mx-auto mb-3" />
        <Skeleton variant="text" width="50%" height={28} className="mx-auto mb-2" />
        <Skeleton variant="text" width="70%" height={14} className="mx-auto" />
      </motion.div>
    ))}
  </div>
);

/**
 * Header Skeleton
 * 
 * A skeleton for page headers with title and subtitle.
 */
export const HeaderSkeleton: React.FC = () => (
  <div className="mb-6">
    <Skeleton variant="text" width="40%" height={32} className="mb-2" />
    <Skeleton variant="text" width="60%" height={18} />
  </div>
);

/**
 * Full Page Skeleton
 * 
 * A complete page loading skeleton combining header and content.
 */
export const FullPageSkeleton: React.FC<{ type?: 'cards' | 'list' | 'chat' | 'games' }> = ({ 
  type = 'cards' 
}) => (
  <div className="p-4 space-y-6">
    <HeaderSkeleton />
    {type === 'cards' && <CardSkeleton count={3} />}
    {type === 'list' && <ListSkeleton count={5} />}
    {type === 'chat' && <ChatSkeleton />}
    {type === 'games' && <GameCardSkeleton count={4} />}
  </div>
);

export default Skeleton;
